import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const SmsOptIn = () => {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const { data } = await axios.post('/api/public/sms-opt-in', {
        phone: phone.trim(),
        email: email.trim() || undefined,
        consent: true
      });
      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Something went wrong.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Unable to complete signup. Please try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mb-6">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">You're signed up</h1>
          <p className="text-gray-600 mb-6">
            You'll receive text messages from FieldHouse about team updates, event reminders, and important announcements.
            Message frequency varies. Msg &amp; data rates may apply.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Reply STOP to cancel or HELP for help at any time. You can also update your notification preferences in your account.
          </p>
          <Link
            to="/login"
            className="inline-block text-primary-600 hover:text-primary-700 font-medium"
          >
            Return to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign up for FieldHouse text messages</h1>
        <p className="text-gray-600 mb-6">
          Get team updates, event reminders, schedule changes, and important announcements from FieldHouse,
          your coaches, and organization administrators via SMS.
        </p>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
          <p className="font-medium text-gray-900 mb-2">By signing up you agree to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Receive recurring text messages from FieldHouse about team and organization activity</li>
            <li>Message frequency varies</li>
            <li>Msg &amp; data rates may apply</li>
            <li>Reply HELP for help or STOP to cancel</li>
            <li>This consent is optional and is not a condition of registering for or using FieldHouse</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Mobile number <span className="text-red-500">*</span>
            </label>
            <input
              id="phone"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-gray-400">(optional — link to existing account)</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-start">
            <input
              id="consent"
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="h-4 w-4 mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="consent" className="ml-2 block text-sm text-gray-700">
              I would like to receive recurring SMS/text message updates from FieldHouse about the
              organizations and teams with which I (or my player) am registered or assigned, including
              schedule changes, event reminders, and announcements. Message frequency varies. Msg &amp; data
              rates may apply. Reply HELP for help or STOP to cancel. This consent is optional and is not a
              condition of registering for or using FieldHouse.
            </label>
          </div>
          {error && (
            <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 px-4 rounded-md bg-primary-600 text-white font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Signing up…' : 'Sign up for texts'}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700">
            Privacy Policy
          </Link>
          <span className="mx-2">|</span>
          <Link to="/terms-of-service" className="text-primary-600 hover:text-primary-700">
            Terms of Service
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SmsOptIn;
